import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { PREFERENCE_V2_NAMES, preferencesV2Selectors, usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { tracker } from 'app-components/utils/core';
import { useAppSelector } from 'hooks/redux';

import { useIsInteractive } from './HoverContext';

type NavToggleSource = 'button' | 'section-header' | 'nav-spacer' | 'menu-button' | 'hotkey';

type NavExpansionContextType = {
  collapsible: boolean;
  canExpand: boolean;
  expanded: boolean;
  toggle: (source: NavToggleSource) => void;
  compact: boolean;
  overlayOpen: boolean;
  openOverlay: (source: NavToggleSource) => void;
  closeOverlay: () => void;
};

const NavExpansionContext = createContext<NavExpansionContextType | undefined>(undefined);

export const NAV_EXPANDED_PREFERENCE = PREFERENCE_V2_NAMES.sNavExpandedWeb;

export const NavExpansionProvider = ({
  children,
  defaultExpanded = true,
  navigationAvailable = true,
}: {
  children: ReactNode;
  defaultExpanded?: boolean;
  navigationAvailable?: boolean;
}): React.JSX.Element => {
  const theme = useTheme();
  const collapsible = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'navLeftNavRedesignExperiment') === 'Variant A',
  );
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  // Until QCS defines the preference, keep the state in memory so toggling never posts a rejected override.
  const [storedExpanded, setStoredExpanded] = usePreferenceV2(NAV_EXPANDED_PREFERENCE, defaultExpanded);
  const isPreferenceDefined = useAppSelector((state) =>
    Boolean(preferencesV2Selectors.getPreferenceObj(state, NAV_EXPANDED_PREFERENCE)),
  );
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const isExpanded = isPreferenceDefined ? Boolean(storedExpanded) : localExpanded;
  const [overlayOpen, setOverlayOpen] = useState(false);

  const compact = collapsible && isNarrow && navigationAvailable;
  const canExpand = collapsible && !isNarrow;
  const expanded = compact || (canExpand && isExpanded);

  const openOverlay = useCallback(
    (source: NavToggleSource) => {
      if (!compact) return;
      tracker.track(tracker.events.navMenuClick, { menuItem: 'Expand', section: 'main', source });
      setOverlayOpen(true);
    },
    [compact],
  );
  const closeOverlay = useCallback(() => setOverlayOpen(false), []);

  useEffect(() => {
    if (!compact) {
      setOverlayOpen(false);
    }
  }, [compact]);

  const toggle = useCallback(
    (source: NavToggleSource) => {
      if (compact) {
        tracker.track(tracker.events.navMenuClick, { menuItem: 'Collapse', section: 'main', source });
        setOverlayOpen(false);
        return;
      }
      if (!canExpand) return;
      tracker.track(tracker.events.navMenuClick, {
        menuItem: expanded ? 'Collapse' : 'Expand',
        section: 'main',
        source,
      });
      if (isPreferenceDefined) {
        setStoredExpanded(!expanded);
      } else {
        setLocalExpanded(!expanded);
      }
    },
    [canExpand, compact, expanded, isPreferenceDefined, setStoredExpanded],
  );

  const value = useMemo(
    () => ({
      collapsible,
      canExpand,
      expanded,
      toggle,
      compact,
      overlayOpen: compact && overlayOpen,
      openOverlay,
      closeOverlay,
    }),
    [collapsible, canExpand, expanded, toggle, compact, overlayOpen, openOverlay, closeOverlay],
  );

  return <NavExpansionContext.Provider value={value}>{children}</NavExpansionContext.Provider>;
};

export const useNavExpansion = (): NavExpansionContextType => {
  const context = useContext(NavExpansionContext);
  if (!context) {
    throw new Error('useNavExpansion must be used within a NavExpansionProvider');
  }
  return context;
};

export const useIsNavExpanded = (): boolean => {
  const context = useContext(NavExpansionContext);
  return Boolean(context?.expanded && !context.compact);
};

export const useNavOverlay = (): Pick<NavExpansionContextType, 'compact' | 'overlayOpen' | 'openOverlay'> | null => {
  const context = useContext(NavExpansionContext);
  return context
    ? { compact: context.compact, overlayOpen: context.overlayOpen, openOverlay: context.openOverlay }
    : null;
};

export const useIsNavOpen = (): boolean => {
  const { collapsible, expanded } = useNavExpansion();
  const isInteractive = useIsInteractive(!collapsible);
  return collapsible ? expanded : isInteractive;
};
